import { z } from 'zod'
import { Sha256 } from './document.js'
import { RowId, FieldId } from './id.js'
import { Moment, Person } from './moment.js'

/**
 * Het ingevulde antwoord, los van de revisie die het bevriest (R-95, ontwerp 4.5).
 *
 * Hier en niet in `revisie.ts`, want `Instantie` (M9) heeft dit begrip ook nodig voor
 * `voorgangerRevisie`, en `revisie.ts` importeert op zijn beurt `Instantie` voor
 * `HandoverOutcome`. Beide zouden naar elkaar wijzen als dit hier niet stond; deze module
 * blijft daarom een blad, met alleen `id.ts`, `document.ts` en `moment.ts` als afhankelijkheid.
 */

/** De zes gebeurtenissen die een revisie afsluiten (R-173). */
export const Event = z.enum([
  'ingediend',
  'teruggelegd',
  'aangevuld',
  'opnieuw-ingediend',
  'goedgekeurd',
  'afgekeurd',
])
export type Event = z.infer<typeof Event>

/**
 * Welke werkverzameling de ontvanger bij een overdracht standaard krijgt (D-59, R-176).
 *
 * Drie waarden, en ze staan hier en niet in de engine omdat de definitie ze draagt: de
 * inrichter stelt ze per overdracht in en de engine rekent ermee. Zou het begrip in de engine
 * blijven en de instelling in het model staan, dan waren er twee namen voor één ding.
 *
 * **Het is een focus en geen afscherming** (R-177). Wat er niet in zit blijft bereikbaar; de
 * werkverzameling zegt alleen wat de ontvanger als eerste voor zich krijgt. `alles` betekent
 * dus niet meer rechten maar geen focus.
 */
export const WerkverzamelingRule = z.enum([
  'openstaande-criteria',
  'openstaand-of-gewijzigd',
  'alles',
])
export type WerkverzamelingRule = z.infer<typeof WerkverzamelingRule>

/**
 * De instelling per overdracht, zoals hij op een definitie staat (D-59).
 *
 * Partieel: wat er niet in staat volgt de standaardregel uit het eisenboek (`standaardregel` in
 * de engine, ontwerp 7.5). Dat is het verschil tussen een instelling en een ingebakken uitkomst,
 * en het is ook de reden dat een lege instelling en een ontbrekende instelling hetzelfde
 * betekenen: de inrichter die niets kiest krijgt wat het eisenboek zegt.
 *
 * Alle zes gebeurtenissen mogen erin. Dat bij goedgekeurd en afgekeurd het werk klaar is, is
 * geen reden om ze hier te weren: dat is beleid, en het schema hoort geen beleid te maken dat
 * niemand heeft vastgesteld. Welke overdrachten de configurator aanbiedt is een keuze van dat
 * scherm en staat daar opgeschreven.
 */
export const WerkverzamelingSetting = z.partialRecord(Event, WerkverzamelingRule)
export type WerkverzamelingSetting = z.infer<typeof WerkverzamelingSetting>

/**
 * Wie de waarde als laatste raakte en wanneer.
 *
 * Elke bevroren waarde draagt dit mee, en dat is wat R-97 (dat en wanneer
 * gewijzigd) en R-188 (de wijziging van een medeschrijver traceerbaar) waarmaakt
 * zonder aparte wijzigingstabel. Voor rich text komt het uit het ychange-schema,
 * voor de overige waarden uit de sleutels die de pod naast de waarde schrijft
 * (`vld-x@door`, `vld-x@op`, ontwerp 4.6), altijd uit het token en nooit uit
 * invoer van de client.
 */
const origin = { gewijzigdOp: Moment, door: Person }

/**
 * Eén ProseMirror/TipTap-knoop, generiek en zonder knooptypen bij naam te noemen.
 *
 * Welke `type`-waarden geldig zijn bepaalt het schema van de editor zelf
 * (`packages/renderer/src/veld/controls/richtext/schema.ts`), niet dit model; dit schema bewaakt
 * alleen de vorm die ProseMirror zelf altijd aanhoudt (`type`, optioneel `attrs`/`marks`/`text`/
 * `content`). Een tweede, domeinspecifieke opsomming hier zou hetzelfde begrip twee keer
 * vastleggen.
 */
export interface ProseMirrorNode {
  readonly type: string
  readonly attrs?: Record<string, unknown> | undefined
  readonly marks?: readonly { readonly type: string; readonly attrs?: Record<string, unknown> | undefined }[] | undefined
  readonly text?: string | undefined
  readonly content?: readonly ProseMirrorNode[] | undefined
}

export const ProseMirrorNode: z.ZodType<ProseMirrorNode> = z.lazy(() =>
  z.strictObject({
    type: z.string(),
    attrs: z.record(z.string(), z.unknown()).optional(),
    marks: z
      .array(z.strictObject({ type: z.string(), attrs: z.record(z.string(), z.unknown()).optional() }))
      .optional(),
    text: z.string().optional(),
    content: z.array(ProseMirrorNode).optional(),
  }),
)

/**
 * Een enkele waarde. Rich text komt hier als tekst binnen: leesbaar zonder Yjs.
 *
 * `structuur` is optioneel en alleen gevuld voor `richtext`: dezelfde inhoud in
 * ProseMirror/TipTap-vorm, naast `waarde` en niet in plaats ervan. Zonder dit veld verdwijnt een
 * atoomknoop (`figuur`, `referentie`) stil bij elke generatienaad, want `waarde` is platte tekst
 * zonder structuur en draagt zo'n knoop niet. Elk ander controltype laat dit veld leeg.
 */
/**
 * Eén gegeven akkoord per persoon: wie, met welk e-mailadres, wanneer, en over welke stand (eisenboek 12.56).
 *
 * `stand` is de hash van de inhoud van de instantie op het moment van het akkoord (`standHash` in de
 * engine). Wijkt de inhoud daarna af, dan is het akkoord vervallen. Persoon, e-mailadres, moment en hash
 * zet de pod uit het token en zijn eigen klok, nooit de browser (R-154). Het e-mailadres staat erbij omdat
 * de lijst het als naam toont, aan beide kanten (besluit van de product owner van 3 november 2026).
 */
export const ConsentStamp = z.strictObject({
  persoon: Person,
  email: z.string().min(3).max(320),
  gegevenOp: Moment,
  stand: Sha256,
  /**
   * Namens wie het akkoord is gegeven, als de persoon een gemachtigde is (R-190, R-191): de persoon uit
   * zijn toekenning, door de pod uit het register gezet en nooit uit de browser. Dan telt het akkoord ook
   * namens die persoon (besluit van de product owner van 3 november 2026, eisenboek 12.56).
   */
  namens: Person.optional(),
})
export type ConsentStamp = z.infer<typeof ConsentStamp>

/**
 * Een enkele waarde. Rich text komt hier als tekst binnen: leesbaar zonder Yjs.
 *
 * `akkoorden` en `stand` alleen bij een akkoord per persoon (eisenboek 12.56): de stempels per persoon
 * staan in de revisie, waar de herkomst ook staat, en `stand` is de hash van de inhoud zoals de pod die
 * het laatst uitrekende. Een akkoord geldt als zijn `stand` die van het antwoord is, en `waarde` zegt of
 * er minstens één geldig akkoord is. Elk ander controltype laat ze leeg, en een revisie van vóór die dag
 * draagt ze niet.
 */
const Value = z.strictObject({
  ...origin,
  waarde: z.union([z.string(), z.number(), z.boolean(), z.null()]),
  structuur: ProseMirrorNode.optional(),
  akkoorden: z.array(ConsentStamp).optional(),
  stand: Sha256.optional(),
})

/**
 * Een meerwaardig antwoord: de gekozen opties van een `multiselect` of een
 * aanvinklijst, of de trefwoorden van een `tag` (R-15).
 *
 * Een eigen sleutel en niet een array in `waarde`, zoals `documenten` en `rijen`
 * ook een eigen sleutel hebben. Daarmee blijft `waarde` één ding: een lezer weet
 * uit de sleutel wat hij heeft en hoeft niet op `Array.isArray` te splitsen, en
 * het schema kan een tekstveld met de waarde `["a"]` blijven weigeren.
 *
 * De sleutel `waarden` komt twee keer voor in dit bestand, en dat is bewust: hier
 * als de lijst waarden van één veld, en in `Rows` als de veldenmap van één rij.
 * De nesting maakt het onderscheid, en het type doet dat ook: hier een array,
 * daar een map op veld-id. De alternatieven waren de sleutel van de rijmap
 * omdopen, en dat is een tweede breuk in een vorm die ontwerp 4.5 al toont.
 *
 * Geen `.min(1)`: wie alles weer uitvinkt laat een lege lijst achter, en dat is
 * een geldige stand. Leeg is leeg, net als een veld dat er niet staat.
 *
 * Zonder boolean: een verzameling ja-en-nee-waarden betekent niets. Een losse
 * instemming is een `Waarde`.
 */
const Values = z.strictObject({
  ...origin,
  waarden: z
    .array(z.union([z.string(), z.number()]))
    .refine((values) => new Set(values).size === values.length, {
      error: 'dezelfde waarde staat er twee keer in',
    }),
})

/**
 * Eén verwijzing naar een bestand in de content-addressed opslag (U25, R-199).
 *
 * De hash is een volledige SHA-256, want een afgekapte hash is niet
 * content-addressed. Ontwerp 4.5 kort hem in het voorbeeld af voor de leesbaarheid.
 *
 * **Naam en omvang staan erbij, en dat is nodig op twee plekken.** De evaluator
 * moet de bestandseisen kunnen toetsen (R-104): typen, aantal en maximale grootte
 * staan als parameters op de regel, en met alleen een hash is daarvan alleen het
 * aantal te toetsen. De evaluator is een pure functie over de stand, zonder I/O
 * per regel (ontwerp 5.5), en hij draait ook in de browser (5.9), dus opzoeken in
 * de opslag kan niet. En een revisie moet over jaren zelfstandig leesbaar zijn
 * (4.5); `sha256:9c4d84...` zegt niet welk bestand er hing, een bestandsnaam wel.
 *
 * De naam hoort bij de verwijzing en niet bij het bestand: twee mensen kunnen
 * hetzelfde bestand onder een andere naam aanhangen. De omvang kan niet uiteen
 * lopen met de opslag, want een content-addressed bestand verandert niet.
 *
 * Een bestandsnaam kan persoonsgegevens bevatten. Dat is geen nieuwe categorie,
 * het bestand zelf staat er al, maar de geanonimiseerde weergave moet de naam
 * kunnen weglaten (R-114); dat is werk van de projectie, niet van dit schema.
 *
 * Geëxporteerd als schema en niet alleen als type, want de renderer moet een lijst bijlagen in een
 * verwijzing herkennen (R-55): in een blokdocument staat een bijlageveld als kale lijst van deze
 * verwijzingen, zonder sleutel `documenten` eromheen (`bouwBlokdocument`). Herkennen aan de vorm
 * met dit schema, en niet met een tweede beschrijving van dezelfde drie velden.
 */
export const DocumentVerwijzing = z.strictObject({
  hash: Sha256,
  /** Een naam en geen pad: geen schuine strepen en geen stuurtekens, en niet `.` of `..`. */
  bestandsnaam: z
    .string()
    .regex(/^(?!\.{1,2}$)[^/\\\u0000-\u001f]{1,255}$/, 'een bestandsnaam is een naam en geen pad'),
  /**
   * Nul is toegestaan: een leeg bestand komt voor, en de stand hoort te kunnen
   * vastleggen wat er werkelijk hing.
   */
  grootteBytes: z.number().int().nonnegative(),
})
export type DocumentVerwijzing = z.infer<typeof DocumentVerwijzing>

/** Een bijlageveld bewaart verwijzingen en nooit het bestand (U25, R-199). */
const Documents = z.strictObject({
  ...origin,
  documenten: z.array(DocumentVerwijzing),
})

const RowValue = z.union([Value, Values, Documents])

/** Een herhaalgroep: rijen met een stabiel rij-id, zodat verwijzingen een naad overleven (4.6). */
const Rows = z.strictObject({
  rijen: z.array(z.strictObject({ rijId: RowId, waarden: z.record(FieldId, RowValue) })),
})

export const Answer = z.union([Value, Values, Documents, Rows])
export type Answer = z.infer<typeof Answer>
